// Nhãn tiếng Việt dùng chung cho hồ sơ nhân sự: vai trò, loại hợp đồng, và
// phạm vi chi nhánh lấy từ ảnh chụp Casdoor.
//
// Vì sao đứng riêng: ba bảng này trước nằm trong biểu mẫu sửa hồ sơ và trong
// thẻ danh sách. Thẻ chi tiết cần đúng những nhãn đó — chép lại là mở đường
// cho hai nơi trôi khác nhau, rồi cùng một người hiện "branch_manager" ở màn
// này và "Trưởng cửa hàng" ở màn kia.
import { ROLE } from "@/lib/auth/roles";

/**
 * Nhãn vai trò — bảng THẬT nằm ở `lib/auth/role-labels.ts` (một mã một tên,
 * 09/10/2026). Xuất lại ở đây để các màn Nhân sự giữ nguyên đường import.
 */
export { ROLE_LABELS, roleLabel } from "@/lib/auth/role-labels";

/**
 * Nhãn chức danh HRM (`staff.job_title_code`, QĐ 0425). Chức danh KHÔNG phải
 * vai: Trưởng ca giữ vai `staff` + phòng của mình. Chỉ HRM gán, qua Center.
 */
export const JOB_TITLE_LABELS: Record<string, string> = {
  truong_ca: "Trưởng ca",
};

export function jobTitleLabel(code: string | null | undefined): string {
  return JOB_TITLE_LABELS[(code ?? "").trim().toLowerCase()] ?? "";
}

/**
 * Ba chức vụ CHỈ HRM gán (CEO chốt 2a, 19/10/2026): Trưởng cửa hàng, Quản lý
 * cụm (vai) và Trưởng ca (chức danh). Hồ sơ HRM làm chủ thì màn Nhân sự không
 * đổi được vai của họ — đổi ở HRM rồi Center gửi sang.
 */
export function isHrmAssignedPosition(role: string | null | undefined, jobTitleCode: string | null | undefined): boolean {
  const r = (role ?? "").trim().toLowerCase();
  return r === ROLE.BranchManager || r === ROLE.AreaManager || jobTitleLabel(jobTitleCode) !== "";
}

/**
 * Nhãn loại hợp đồng. Giữ đúng chữ mà ô chọn ở biểu mẫu sửa hồ sơ vẫn dùng
 * ("Full-time"), KHÔNG dịch sang tiếng Việt ở đây: người đã quen chữ nào trên
 * ô chọn thì phải thấy đúng chữ đó ở hồ sơ chi tiết. Muốn đổi cách gọi thì đổi
 * ở đây, cả hai màn theo cùng lúc.
 */
export const EMPLOYMENT_LABELS: Record<string, string> = {
  fulltime: "Full-time",
  parttime: "Part-time",
  ctv: "CTV",
};

/**
 * Câu nhắc dưới ô "Lương cứng" cho năm phòng mà bảng lương KHÔNG đọc ô này
 * (kiểm thử toàn vai 16/09/2026, lỗi #18): máy tính lương lấy lương cứng của
 * năm phòng này từ bảng luật ở trang "Cơ chế lương v2" (`salary_rules_v2`) —
 * ảnh thẻ / makeup theo loại hợp đồng, sửa hình / CSKH theo vai, Photo Profile
 * không có lương cứng (`pkg/engines/payroll/department_*.go`). Không nhắc thì
 * người sửa hồ sơ tưởng đã đổi lương.
 *
 * Phòng khác (sale, kế toán, marketing, lễ tân, ban điều hành) đọc đúng ô này
 * → null. Đổi luật lương cứng của phòng nào ở máy chủ thì đổi ở đây cùng lúc.
 */
/**
 * Người vai QC (QĐ 0255): ô lương cứng là lương cứng QC và VÀO bảng lương từ
 * tháng 2026-09 — khác năm phòng ở `baseSalaryRuleNote`. Không bắt buộc điền:
 * để trống thì máy lương giữ cách trả cũ và trang Bảng lương hiện dải nhắc.
 */
export const QC_BASE_SALARY_NOTE =
  "Người vai QC: số ở ô này là lương cứng QC hằng tháng và vào bảng lương từ tháng 9/2026. Để trống thì bảng lương giữ cách trả cũ và trang Bảng lương hiện dải nhắc.";

/**
 * Người vai trưởng cửa hàng / quản lý cụm (QĐ 0322): từ tháng 2026-10 máy lương
 * tính họ ở bảng Ban điều hành với lương cứng ở ô này, dù hồ sơ ghi phòng nào
 * (`payrollDeptOf`). Chỉ thay câu nhắc của năm phòng bên dưới — câu cũ nói "không
 * vào bảng lương", sai với hai vai này; ô vẫn không bắt buộc ở đó vì để trống thì
 * máy giữ cách trả của phòng. Phòng vốn đọc ô này giữ nguyên: không nhắc, bắt buộc.
 */
export const MANAGER_BASE_SALARY_NOTE =
  "Người vai trưởng cửa hàng hoặc quản lý cụm: số ở ô này vào bảng lương (bảng Ban điều hành) từ tháng 10/2026. Để trống thì bảng lương giữ cách trả của phòng này.";

export function baseSalaryRuleNote(
  department: string | null | undefined,
  role?: string | null | undefined,
): string | null {
  const r = (role ?? "").trim().toLowerCase();
  if (r === ROLE.QC) return QC_BASE_SALARY_NOTE;
  const note = deptBaseSalaryNote(department);
  if (note !== null && (r === ROLE.BranchManager || r === ROLE.AreaManager)) {
    return MANAGER_BASE_SALARY_NOTE;
  }
  return note;
}

function deptBaseSalaryNote(department: string | null | undefined): string | null {
  switch ((department ?? "").trim()) {
    case "photo_idphoto":
    case "makeup":
      return "Phòng này tính lương cứng theo loại hợp đồng ở trang Cơ chế lương v2 — số gõ ở ô này không vào bảng lương.";
    case "retouch":
    case "cskh":
      return "Phòng này tính lương cứng theo vai (trưởng phòng hay nhân viên) ở trang Cơ chế lương v2 — số gõ ở ô này không vào bảng lương.";
    case "photo_profile":
      return "Phòng Chụp ảnh profile không có lương cứng, trả theo từng buổi chụp — số gõ ở ô này không vào bảng lương.";
    default:
      return null;
  }
}

/**
 * Ô "Lương cứng" có BẮT BUỘC ở phòng này không (anh chốt 23/09/2026): bắt điền
 * đúng ở những phòng mà bảng lương ĐỌC ô này — để trống ở đó là lỡ lưu 0đ lương
 * cứng cho người thật sự nhận lương cứng. Năm phòng có câu nhắc ở
 * `baseSalaryRuleNote` thì trống = 0đ, vì số ở ô đó không vào bảng lương.
 *
 * Đi theo đúng `baseSalaryRuleNote` để hai luật không trôi khỏi nhau. Chưa chọn
 * phòng thì chưa bắt: ô Phòng ban tự báo lỗi của nó, chọn xong mới biết ô lương
 * có vào bảng lương hay không.
 */
export function baseSalaryRequired(
  department: string | null | undefined,
  role?: string | null | undefined,
): boolean {
  const dept = (department ?? "").trim();
  return dept !== "" && baseSalaryRuleNote(dept, role) === null;
}

export function employmentLabel(employment: string | null | undefined): string {
  const key = (employment ?? "").trim();
  if (!key) return "";
  return EMPLOYMENT_LABELS[key] ?? key;
}

type BranchScopeLike =
  | {
      scopeCodes?: string[];
      branchCodes?: string[];
      status?: string | null;
    }
  | null
  | undefined;

/**
 * Chữ mô tả phạm vi chi nhánh của một người, đọc từ ảnh chụp Casdoor.
 *
 * Ưu tiên `scopeCodes` (thứ Casdoor thật sự cấp) rồi mới tới `branchCodes`
 * (danh sách đã bung ra). Trả chuỗi rỗng khi chưa có gì, để nơi gọi tự quyết
 * hiện "Chưa đồng bộ" hay bỏ trống hẳn.
 *
 * `branchName` là hàm tra danh mục cơ sở (`useBranchLabel`) do nơi gọi đưa vào.
 * Trước đây hàm này tra bảng tên TĨNH trong statusMap — bảng đó không có cơ sở
 * mở sau (Trường Chinh, Lê Văn Sỹ 2) nên thẻ nhân sự hiện "Phạm vi: hn_tc"
 * (kiểm thử toàn vai 17/09/2026).
 */
export function casdoorScopeLabel(
  scope: BranchScopeLike,
  branchName: (code: string) => string,
): string {
  const scopeCodes = scope?.scopeCodes ?? [];
  if (scopeCodes.includes("all")) return "Tất cả chi nhánh";

  const groupLabels = scopeCodes
    .filter((code) => code.startsWith("group-"))
    .map((code) => groupScopeLabel(code.slice("group-".length)));
  if (groupLabels.length > 0) return groupLabels.join(", ");

  const branchCodes =
    scopeCodes.length > 0
      ? scopeCodes
          .filter((code) => code.startsWith("branch-"))
          .map((code) => code.slice("branch-".length))
      : (scope?.branchCodes ?? []);
  if (branchCodes.length === 0) return "";
  return branchCodes.map((code) => branchName(code)).join(", ");
}

/**
 * Dòng "Chi nhánh làm việc" của một người (QĐ 0516) — gộp HAI nguồn chỉ đọc:
 * phạm vi Casdoor (các cơ sở được vào) và `staff.branch` (chi nhánh chính, nơi
 * lương và thi đua tính người đó vào). Cả hai do HRM ghi; màn Nhân sự chỉ xem.
 *
 * - `scope`: chữ phạm vi như `casdoorScopeLabel` ("" = chưa đồng bộ).
 * - `primary`: tên chi nhánh chính ("" = hồ sơ chưa có).
 * - `primaryOutside`: chi nhánh chính KHÔNG nằm trong phạm vi — người đó được
 *   tính ở một cơ sở họ không mở được. Chỉ báo khi đọc được phạm vi dạng danh
 *   sách; phạm vi rỗng hay chưa đồng bộ thì không kết luận gì.
 */
export function workingBranchesView(
  primaryCode: string | null | undefined,
  scope: BranchScopeLike,
  branchName: (code: string) => string,
): { scope: string; primary: string; primaryOutside: boolean } {
  const primary = (primaryCode ?? "").trim();
  const branchCodes = scope?.branchCodes ?? [];
  return {
    scope: casdoorScopeLabel(scope, branchName),
    primary: primary ? branchName(primary) : "",
    primaryOutside: primary !== "" && branchCodes.length > 0 && !branchCodes.includes(primary),
  };
}

function groupScopeLabel(code: string): string {
  switch (code) {
    case "hn":
      return "Hà Nội";
    case "sg":
      return "TP.HCM";
    default:
      return code;
  }
}

/**
 * Từ điển nhãn THẺ của màn Nhân sự.
 *
 * Vì sao tách khỏi component: bài hướng dẫn gọi tên thẻ theo đúng chữ trên
 * màn, nên gõ chuỗi ở hai nơi thì đổi một nơi là bài lặng lẽ chỉ tới một thẻ
 * không còn tồn tại — không có gì nổ, vì chữ trong bài chỉ là chuỗi.
 *
 * Khoá ở đây chính là `value` của `TabsTrigger` trên màn, nên hộp hướng dẫn mở
 * thẳng được vào thẻ người đọc đang đứng.
 *
 * Khai ĐỦ mọi thẻ của màn, kể cả thẻ chưa có bài hướng dẫn: từ điển này tả MÀN, còn chuyện
 * thẻ nào có bài là việc của `page-guide/staff-guide-tabs.ts`. Trộn hai câu
 * hỏi đó vào nhau thì lần sau ai thêm thẻ vào màn sẽ phải nhớ sửa cả hai nơi.
 */
export const STAFF_TAB_LABEL = {
  staff: "Nhân sự",
  branches: "Chi nhánh",
  "data-sync": "Đồng bộ dữ liệu",
  jobs: "Việc nhận",
} as const;

export type StaffTabKey = keyof typeof STAFF_TAB_LABEL;
